// A header value the browser will send as is: tab, printable ASCII and the printable Latin-1 range.
// Anything else (a character above U+00FF, or a control character) makes XMLHttpRequest.setRequestHeader
// and fetch throw "String contains non ISO-8859-1 code point" / "Invalid value", and the request is
// never sent (AM-5103).
const SENDABLE_AS_IS = /^[\t\x20-\x7E\xA0-\xFF]*$/;

/**
 * Makes a value safe to send as an HTTP header value from a browser.
 *
 * - A value of tab, printable ASCII and printable Latin-1 only is returned unchanged, byte for byte as before
 *   (including a value that already holds a `%XX` sequence, such as a landing page path from location.pathname).
 * - Anything else is returned as `encodeURIComponent(value)`. The API decodes the tracking headers it reads
 *   (`getUtmParams`); ids and tokens are ASCII by construction, so for them this is a no-op in practice, and a
 *   pathological value now fails one lookup instead of failing every request.
 * - A value that cannot be encoded (an unpaired UTF-16 surrogate) returns `null`: the caller skips the header.
 *
 * Only unsendable values change, so this ships safely before the API decodes: values that worked before are
 * sent exactly as before.
 */
export function toSafeHeaderValue(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) {
    return null;
  }

  if (SENDABLE_AS_IS.test(value)) {
    return value;
  }

  try {
    return encodeURIComponent(value);
  } catch {
    return null;
  }
}
